/**
 * Past-date menu GET policy (before today, local YYYY-MM-DD):
 * - Fetch once per app session (staleTime ∞ while JS bundle lives).
 * - Do not invalidate via bulk rules (checkout, freeze, dashboard refresh, etc.).
 * - Refetch only after app reload, or calendar rating popup save on that date.
 */
import type { QueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { ensureAsciiDateString } from '../../../shared/utils/apiParams';
import { menuKeys } from './keys';
import { policies } from '../../query/policies';
import { useMenuStore } from './store';

/** Dashboard macro chart window: 7 days ending today (6 prior + today). */
export const MACRO_WEEK_DAY_COUNT = 7;

export const PAST_DATE_MENU_STALE_MS = Number.POSITIVE_INFINITY;

export function todayYmd(): string {
  return moment().format('YYYY-MM-DD');
}

export function buildMacroWeekDateList(reference = moment()): string[] {
  const list: string[] = [];
  for (let offset = MACRO_WEEK_DAY_COUNT - 1; offset >= 0; offset--) {
    list.push(reference.clone().subtract(offset, 'days').format('YYYY-MM-DD'));
  }
  return list;
}

export function isPastMenuDate(date: string, today_ymd = todayYmd()): boolean {
  const date_ascii = ensureAsciiDateString(date) || date;
  if (date_ascii.length < 10) {
    return false;
  }
  return date_ascii < today_ymd;
}

/** React Query stale/gc for menu GET — past dates stay fresh for the app session. */
export function menuCacheTimingForDate(date: string): {
  staleTime: number;
  gcTime: number;
  isPast: boolean;
} {
  const date_ascii = ensureAsciiDateString(date) || date;
  const is_past = isPastMenuDate(date_ascii);
  return {
    isPast: is_past,
    staleTime: is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.staleTime,
    gcTime: is_past ? PAST_DATE_MENU_STALE_MS : policies.menu.gcTime,
  };
}

/** Past-date menu already in cache — skip background prefetch/refetch. */
export function hasPastDateMenuCached(
  query_client: QueryClient,
  customer_id: number,
  date: string,
  is_double_box = false,
): boolean {
  if (!isPastMenuDate(date)) {
    return false;
  }
  const date_ascii = ensureAsciiDateString(date) || date;
  return query_client.getQueryData(menuKeys.day(customer_id, date_ascii, is_double_box)) != null;
}

/** Bulk/menu-date invalidation skips past dates unless explicitly forced (rating). */
export function isMenuInvalidationAllowedForDate(date: string): boolean {
  return !isPastMenuDate(date);
}

export function invalidateMenuQueryForDate(
  query_client: QueryClient,
  customer_id: number,
  date: string,
): void {
  const date_ascii = ensureAsciiDateString(date) || date;
  void query_client.invalidateQueries({ queryKey: menuKeys.day(customer_id, date_ascii, false) });
  void query_client.invalidateQueries({ queryKey: menuKeys.day(customer_id, date_ascii, true) });
}

/**
 * Calendar rating saved on a past date — allowed refetch for that day only.
 */
export async function refetchPastDateMenuAfterRating(
  query_client: QueryClient,
  customer_id: number,
  date: string,
): Promise<void> {
  if (!isPastMenuDate(date)) {
    return;
  }
  invalidateMenuQueryForDate(query_client, customer_id, date);
  const date_ascii = ensureAsciiDateString(date) || date;
  await query_client.refetchQueries({
    queryKey: menuKeys.day(customer_id, date_ascii, false),
  });
}

/**
 * Predicate for `invalidateQueries` — matches menu keys for today/future only.
 */
export function menuQueryPredicateSkipPastDates(customer_id: number) {
  return (query: { queryKey: readonly unknown[] }) => {
    const key = query.queryKey;
    if (key[0] !== 'menu' || key[1] !== customer_id) {
      return false;
    }
    const date = typeof key[2] === 'string' ? key[2] : '';
    return isMenuInvalidationAllowedForDate(date);
  };
}

type CachedMenu = {
  menu_builders: unknown;
  available_dates?: string[];
  is_double_box?: boolean;
};

export async function patchMenuQueryCacheFromStore(
  query_client: QueryClient,
  customerId: number,
  dateRaw: string,
  isDoubleBox: boolean,
): Promise<void> {
  const date_ascii = ensureAsciiDateString(dateRaw) || dateRaw;
  const slot = useMenuStore.getState().getSlot(date_ascii);
  if (!slot?.menuBuilders) {
    return;
  }
  const key = menuKeys.day(customerId, date_ascii, isDoubleBox);
  await query_client.cancelQueries({ queryKey: key });
  const prev = query_client.getQueryData(key) as CachedMenu | undefined;
  query_client.setQueryData(key, {
    menu_builders: slot.menuBuilders,
    available_dates: prev?.available_dates ?? [],
    is_double_box: prev?.is_double_box ?? isDoubleBox,
  });
}
